import React, { useEffect, useRef, useState } from 'react';
import DatePicker from '../../../helpers/DatePicker';
import { formatDateLine, formatDate } from '../../../helpers/formatDate';

export const DATE_PRESETS = [
	{ key: 'this_week', label: 'This Week' },
	{ key: 'last_week', label: 'Last Week' },
	{ key: 'this_month', label: 'This Month' },
	{ key: 'last_month', label: 'Last Month' },
	{ key: 'last_3_months', label: 'Last 3 Months' },
	{ key: 'last_6_months', label: 'Last 6 Months' },
	{ key: 'this_year', label: 'This Year' },
];

export const getPresetRange = (preset) => {
	const today = new Date();
	const fmt = (d) => formatDateLine(d);

	const startOf = (d, unit) => {
		const n = new Date(d);
		if (unit === 'week') {
			const day = n.getDay();
			n.setDate(n.getDate() - day + (day === 0 ? -6 : 1));
		} else if (unit === 'month') {
			n.setDate(1);
		} else if (unit === 'year') {
			n.setMonth(0, 1);
		}
		n.setHours(0, 0, 0, 0);
		return n;
	};

	const ranges = {
		this_week: () => {
			const s = startOf(today, 'week');
			const e = new Date(s);
			e.setDate(e.getDate() + 6);
			return [fmt(s), fmt(e)];
		},
		last_week: () => {
			const s = startOf(today, 'week');
			s.setDate(s.getDate() - 7);
			const e = new Date(s);
			e.setDate(e.getDate() + 6);
			return [fmt(s), fmt(e)];
		},
		this_month: () => {
			const s = startOf(today, 'month');
			const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
			return [fmt(s), fmt(e)];
		},
		last_month: () => {
			const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
			const e = new Date(today.getFullYear(), today.getMonth(), 0);
			return [fmt(s), fmt(e)];
		},
		last_3_months: () => {
			const s = new Date(today);
			s.setMonth(s.getMonth() - 3);
			return [fmt(s), fmt(today)];
		},
		last_6_months: () => {
			const s = new Date(today);
			s.setMonth(s.getMonth() - 6);
			return [fmt(s), fmt(today)];
		},
		this_year: () => [fmt(startOf(today, 'year')), fmt(today)],
	};

	return ranges[preset] ? ranges[preset]() : [null, null];
};

const DashboardDateRangeDropdown = ({ dateFrom, dateTo, activePreset, onChange, isLoading, onOpenChange }) => {
	const [show, setShow] = useState(false);
	const [openLeft, setOpenLeft] = useState(false);
	const [activeTab, setActiveTab] = useState('presets');

	// local state for custom (important!)
	const [tempFrom, setTempFrom] = useState(null);
	const [tempTo, setTempTo] = useState(null);

	const ref = useRef(null);
	const closeReasonRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (ref.current && !ref.current.contains(e.target)) {
				closeReasonRef.current = 'outside';
				setShow(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const isActive = dateFrom || dateTo;

	const label = activePreset
		? DATE_PRESETS.find(p => p.key === activePreset)?.label
		: (dateFrom && dateTo
			? `${formatDate(dateFrom)} – ${formatDate(dateTo)}`
			: 'Date Range');

	const handlePreset = (presetKey) => {
		if (presetKey === activePreset) {
			handleClear();
			return;
		}
		const [from, to] = getPresetRange(presetKey);
		onChange({ dateFrom: from, dateTo: to, activePreset: presetKey });
		closeReasonRef.current = 'deliberate';
		setShow(false);
	};

	const handleApplyCustom = () => {
		if (!tempFrom || !tempTo) return;
		onChange({
			dateFrom: formatDateLine(tempFrom),
			dateTo: formatDateLine(tempTo),
			activePreset: null,
		});
		closeReasonRef.current = 'deliberate';
		setShow(false);
	};

	const handleClear = () => {
		setTempFrom(null);
		setTempTo(null);
		onChange({ dateFrom: null, dateTo: null, activePreset: null });
		closeReasonRef.current = 'deliberate';
		setShow(false);
	};

	// sync when opening; also notify parent of open state
	useEffect(() => {
		if (show) closeReasonRef.current = null;
		onOpenChange?.(show, closeReasonRef.current);
		closeReasonRef.current = null;
		if (show) {
			setTempFrom(dateFrom ? new Date(dateFrom) : null);
			setTempTo(dateTo ? new Date(dateTo) : null);
		}
	}, [show]);

	return (
		<div ref={ref} className="position-relative d-inline-block">
			<button
				data-filter-toggle="true"
				className={`btn d-flex align-items-center gap-1 ${isActive ? 'btn-primary' : 'btn-secondary'}${show ? ' show' : ''}`}
				style={{ fontSize: '14px', whiteSpace: 'nowrap' }}
				onClick={() => {
					if (!show && ref.current) {
						const rect = ref.current.getBoundingClientRect();
						setOpenLeft(rect.left + 260 > window.innerWidth);
					}
					if (show) closeReasonRef.current = 'deliberate';
					setShow(s => !s);
				}}
				disabled={isLoading}
			>
				{label}
				<span className="ms-1" style={{
					display: 'inline-block',
					width: 0,
					height: 0,
					borderLeft: '4px solid transparent',
					borderRight: '4px solid transparent',
					borderTop: '4px solid currentColor'
				}} />
			</button>

			{show && (
				<div
					className="position-absolute bg-white border rounded shadow-sm p-2"
					style={{
						zIndex: 1050,
						top: '100%',
						left: openLeft ? 'auto' : 0,
						right: openLeft ? 0 : 'auto',
						minWidth: '260px',
						marginTop: '4px'
					}}
					onMouseDown={(e) => e.stopPropagation()}
				>

					{/* Tabs */}
					<div className="d-flex mb-2" style={{ gap: '4px' }}>
						<button
							className={`btn btn-sm flex-fill ${activeTab === 'presets' ? 'btn-primary' : 'btn-light'}`}
							onClick={() => setActiveTab('presets')}
						>
							Presets
						</button>
						<button
							className={`btn btn-sm flex-fill ${activeTab === 'custom' ? 'btn-primary' : 'btn-light'}`}
							onClick={() => setActiveTab('custom')}
						>
							Custom
						</button>
					</div>

					{/* Presets */}
					{activeTab === 'presets' && (
						<div>
							{DATE_PRESETS.map(p => (
								<button
									key={p.key}
									className={`btn btn-sm w-100 text-start mb-1 ${activePreset === p.key ? 'btn-primary' : 'btn-light'}`}
									onClick={() => handlePreset(p.key)}
								>
									{p.label}
								</button>
							))}
						</div>
					)}

					{/* Custom */}
					{activeTab === 'custom' && (
						<div>
							<div className="text-muted small mb-1 text-start">Start date</div>
							<DatePicker
								handleDateChange={setTempFrom}
								selected={tempFrom}
							/>

							<div className="text-muted small mt-2 mb-1 text-start">End date</div>
							<DatePicker
								handleDateChange={setTempTo}
								selected={tempTo}
							/>

							<button
								className="btn btn-primary btn-sm w-100 mt-2"
								disabled={!tempFrom || !tempTo}
								onClick={handleApplyCustom}
							>
								Apply
							</button>
						</div>
					)}

					{/* Clear */}
					{isActive && (
						<button
							className="btn btn-sm btn-link text-danger p-0 mt-2"
							onClick={handleClear}
						>
							Clear
						</button>
					)}

				</div>
			)}
		</div>
	);
};

export default DashboardDateRangeDropdown;